/**
 * 時刻ユーティリティ。
 *
 * 時刻は「その日の00:00からの経過分」で保持する。跨日勤務は 25:00 → 1500 のように
 * 1440 を超える値で表現し、翌日の行には入力しない（ガイドライン §7.4）。
 */
const TIME_PATTERN = /^(\d{1,2}):([0-5]\d)$/;
/** 入力可能な最大時刻（47:59）。翌々日以降は表現しない */
export const MAX_TIME_MINUTES = 47 * 60 + 59;
/**
 * "HH:MM" を分に変換する。跨日表記（25:00、30:00 等）に対応。
 * 空文字・不正値は null を返す。
 */
export function parseTime(value) {
    if (value === null || value === undefined)
        return null;
    const trimmed = value.trim();
    if (trimmed === '')
        return null;
    const matched = TIME_PATTERN.exec(trimmed);
    if (!matched)
        return null;
    const hours = Number(matched[1]);
    const minutes = Number(matched[2]);
    const total = hours * 60 + minutes;
    if (total > MAX_TIME_MINUTES)
        return null;
    return total;
}
/** 分を "HH:MM" に変換する。null は空文字 */
export function formatTime(minutes) {
    if (minutes === null || minutes === undefined || !Number.isFinite(minutes))
        return '';
    const rounded = Math.round(minutes);
    const hours = Math.floor(rounded / 60);
    const mins = rounded % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}
/** 分を「N時間M分」形式に変換する（画面表示用） */
export function formatDuration(minutes) {
    if (minutes === null || minutes === undefined || !Number.isFinite(minutes))
        return '—';
    const rounded = Math.round(minutes);
    const sign = rounded < 0 ? '-' : '';
    const abs = Math.abs(rounded);
    const hours = Math.floor(abs / 60);
    const mins = abs % 60;
    return `${sign}${hours}時間${String(mins).padStart(2, '0')}分`;
}
/** 分を時間（小数）に変換する。集計・給与計算はこの値を使う */
export function minutesToHours(minutes) {
    return minutes / 60;
}
/** 時間（小数）を分に変換する */
export function hoursToMinutes(hours) {
    return hours * 60;
}
/** 小数第2位で丸める（GAS 取込と同じ桁で集計値を確定させる） */
export function round2(value) {
    return Math.round(value * 100) / 100;
}
/** 円未満四捨五入（ガイドライン §6.4） */
export function roundYen(value) {
    return Math.round(value);
}
/** 区間 [a, b) と帯の集合との重なり（分）を求める */
export function overlapMinutes(a, b, bands) {
    if (a === null || b === null || b <= a)
        return 0;
    let sum = 0;
    for (const [from, to] of bands) {
        sum += Math.max(0, Math.min(b, to) - Math.max(a, from));
    }
    return sum;
}
/** YYYY-MM-DD を UTC 基準の Date に変換する（タイムゾーンによる日付ずれを防ぐ） */
export function parseDate(date) {
    const [y, m, d] = date.split('-').map(Number);
    return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
}
/** Date を YYYY-MM-DD に変換する */
export function formatDate(date) {
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}
/** YYYY-MM-DD の日数加算 */
export function addDays(date, days) {
    const d = parseDate(date);
    d.setUTCDate(d.getUTCDate() + days);
    return formatDate(d);
}
/** 曜日番号（月曜=0 … 日曜=6）。週は月曜起算（ガイドライン §3.2） */
export function weekdayIndex(date) {
    return (parseDate(date).getUTCDay() + 6) % 7;
}
/** その日が属する週の月曜日を返す */
export function startOfWeek(date) {
    return addDays(date, -weekdayIndex(date));
}
/** YYYY-MM-DD → YYYY-MM */
export function monthOf(date) {
    return date.slice(0, 7);
}
/** 日本標準時（UTC+9）との時差（ミリ秒） */
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;
/**
 * 日本時間での「今日」を YYYY-MM-DD で返す。
 *
 * サーバーの時計は UTC で動く（Cloud Run）ため、そのまま日付にすると日本時間の
 * 午前0時〜9時のあいだ前日として扱われてしまう。日付はすべてこの関数を通す。
 */
export function todayInJst(now = new Date()) {
    return formatDate(new Date(now.getTime() + JST_OFFSET_MS));
}
/** 日本時間での「今月」を YYYY-MM で返す */
export function currentMonthInJst(now = new Date()) {
    return monthOf(todayInJst(now));
}
/**
 * ISO 8601 の日時を日本時間の「9月7日 11:03」形式にする。
 * サーバーが組み立てる文言（確定条件の説明など）にも UTC の生の値を出さないために使う
 */
export function formatDateTimeJst(iso) {
    if (!iso)
        return '—';
    const time = new Date(iso).getTime();
    if (!Number.isFinite(time))
        return '—';
    const date = new Date(time + JST_OFFSET_MS);
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    return `${date.getUTCMonth() + 1}月${date.getUTCDate()}日 ${hours}:${minutes}`;
}
/** 月の日数 */
export function daysInMonth(month) {
    const [y, m] = month.split('-').map(Number);
    return new Date(Date.UTC(y ?? 1970, m ?? 1, 0)).getUTCDate();
}
/** 月の全日付（YYYY-MM-DD）を返す */
export function datesInMonth(month) {
    const count = daysInMonth(month);
    const dates = [];
    for (let day = 1; day <= count; day += 1) {
        dates.push(`${month}-${String(day).padStart(2, '0')}`);
    }
    return dates;
}
const WEEKDAY_LABELS = ['月', '火', '水', '木', '金', '土', '日'];
/** 曜日ラベル（月〜日） */
export function weekdayLabel(date) {
    return WEEKDAY_LABELS[weekdayIndex(date)] ?? '';
}
//# sourceMappingURL=time.js.map